import React, { useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import PageShell from './PageShell';
import Seo from '../components/Seo';
import { useI18n } from '../context/I18nContext';
import { publicApi } from '../api';
import { useApi } from '../hooks/useApi';
import { Spinner } from '../components/StateHelpers';
import { ArrowLeft, Calendar, Tag as TagIcon, GitCommit } from 'lucide-react';

export default function UpdateDetail() {
  const { slug } = useParams();
  const { lang, tr } = useI18n();
  const { data: u, loading, error } = useApi(() => publicApi.update(slug, lang), [slug, lang]);
  useEffect(() => { if (u) document.title = `${u.title} — Aegis Studios`; }, [u]);
  if (loading) return <PageShell eyebrow="" title="..."><Spinner /></PageShell>;
  if (error || !u) return <Navigate to="/updates" replace />;

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'TechArticle',
    headline: u.title,
    datePublished: u.created_at,
    author: { '@type': 'Organization', name: 'Aegis Studios' },
    articleSection: u.project_name,
  };

  return (
    <PageShell
      eyebrow={<><GitCommit className="w-3 h-3 inline mr-1" /> {u.project_name || 'GÜNCELLEME'}</>}
      title={<span className="text-white">{u.title}</span>}
      subtitle={u.summary}
    >
      <Seo title={u.meta_title || u.title} description={u.meta_description || u.summary || u.title} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />
      <section className="pb-24">
        <div className="max-w-[900px] mx-auto px-4 md:px-6 lg:px-10">
          <Link to="/updates" className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-[#f4d896] mb-8">
            <ArrowLeft className="w-4 h-4" /> {tr('updates.back', 'Tüm güncellemeler')}
          </Link>
          <div className="flex flex-wrap items-center gap-3 mb-8 text-xs">
            {u.version && <span className="px-3 py-1.5 rounded bg-[#e4b44e]/15 border border-[#e4b44e]/30 text-[#f4d896] font-bold tracking-wider">v{u.version}</span>}
            {u.project_name && <span className="px-3 py-1.5 rounded bg-white/5 border border-white/10 text-white/60 inline-flex items-center gap-1.5"><TagIcon className="w-3 h-3" /> {u.project_name}</span>}
            {u.created_at && <span className="text-white/40 tracking-widest inline-flex items-center gap-1.5"><Calendar className="w-3 h-3" /> {String(u.created_at).slice(0, 10)}</span>}
          </div>
          <article className="prose prose-invert prose-p:text-white/75 prose-headings:text-white prose-h2:font-display prose-strong:text-white prose-a:text-[#f4d896] prose-li:text-white/75 prose-ul:my-3 max-w-none"
            dangerouslySetInnerHTML={{ __html: u.content || '' }} />
        </div>
      </section>
    </PageShell>
  );
}
